import * as React from 'react';
import { cn } from '@/util/cn';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  '$tw-inline-flex $tw-items-center $tw-justify-around $tw-whitespace-nowrap $tw-rounded-md $tw-text-sm $tw-font-bold $tw-uppercase $tw-ring-offset-background $tw-transition-colors focus-visible:$tw-outline-none focus-visible:$tw-ring-2 focus-visible:$tw-ring-ring focus-visible:$tw-ring-offset-2 disabled:$tw-pointer-events-none disabled:$tw-border disabled:$tw-border-disabled-border disabled:$tw-bg-disabled disabled:$tw-text-disabled-foreground',
  {
    variants: {
      variant: {
        default:
          '$tw-rounded-lg $tw-bg-primary $tw-text-primary-foreground $tw-shadow hover:$tw-bg-primary/90',
        destructive:
          '$tw-rounded-lg $tw-border $tw-border-destructive-border $tw-bg-destructive $tw-text-destructive-foreground $tw-shadow hover:$tw-bg-destructive/90',
        secondary:
          '$tw-rounded-lg $tw-border $tw-border-foreground/80 $tw-bg-transparent $tw-text-foreground hover:$tw-border-foreground/60 disabled:$tw-border-muted',
        tertiary:
          '$tw-rounded-lg $tw-bg-background $tw-text-foreground $tw-shadow-md hover:$tw-bg-background/80',
        ghost: '$tw-rounded-lg $tw-text-accent-foreground hover:$tw-bg-accent',
        ghostWarning: '$tw-rounded-lg $tw-text-warning-foreground hover:$tw-bg-warning-border/50',
        ghostDestructive:
          '$tw-rounded-lg $tw-text-destructive-foreground hover:$tw-bg-destructive/50',
        link: '$tw-rounded-lg $tw-text-muted $tw-underline-offset-4 hover:$tw-text-foreground hover:$tw-underline',
      },
      size: {
        default: '$tw-h-10 $tw-px-4 $tw-py-2',
        sm: '$tw-h-9 $tw-rounded-md $tw-px-3',
        lg: '$tw-h-11 $tw-rounded-md $tw-px-8',
        icon: '$tw-h-10 $tw-w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
